import React from "react";
import { LinearProgress } from "@mui/material";
import withStyles from "isomorphic-style-loader/withStyles";
import "@fortawesome/fontawesome-free/css/all.min.css";
import styles from "./styles.scss";
import { serviceCaller } from "../../../../../../composition";
import { OnboardingTask } from "../../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare } from "@fortawesome/free-regular-svg-icons";
import ReactTooltip from "react-tooltip";

const OnboardingCard = (task: OnboardingTask) => {
  const {
    cardKey,
    title,
    completedSteps,
    totalSteps,
    color,
    isActive,
    onClick,
    displayState,
    needsAttention,
    locked,
    lockTooltip,
  } = task;

  const progress = Math.round((completedSteps / totalSteps) * 100);
  const tooltipId = `onboarding-card-lock-${cardKey}`;
  const isComplete =
    !locked && (displayState === "completed" || progress === 100);

  const progressLabel = (() => {
    if (locked) {
      return "Locked";
    }
    if (displayState === "attention") {
      return "Needs attention";
    }
    if (displayState === "processing") {
      return "Processing";
    }
    if (displayState === "paused") {
      return "Paused";
    }
    if (displayState === "failed") {
      return "Failed";
    }
    if (isComplete) {
      return "Completed";
    }
    return "Progress";
  })();

  const showSpinner =
    !locked && (displayState === "processing" || displayState === "attention");

  return (
    <div
      className={`${styles.taskItem} ${isActive ? styles.active : ""} ${
        locked ? styles.locked : ""
      }`}
      onClick={locked ? undefined : onClick}
      style={{ cursor: locked || !onClick ? "default" : "pointer" }}
      data-tip={locked && lockTooltip ? true : undefined}
      data-for={locked && lockTooltip ? tooltipId : undefined}
    >
      {needsAttention && !locked && (
        <span className={styles.attentionBadge} aria-label="Needs attention" />
      )}
      <div className={styles.cardHeader}>
        <h6
          className={`${styles.cardTitle} ${locked ? styles.cardTitleLocked : ""}`}
        >
          {title}
        </h6>
      </div>

      <div className={styles.cardProgress}>
        <div className={styles.progressInfo}>
          <small
            className={`${styles.progressLabel} ${
              locked ? styles.progressLabelLocked : ""
            }`}
          >
            {showSpinner ? (
              <span
                className={
                  displayState === "attention"
                    ? styles.attentionLabel
                    : styles.processingLabel
                }
              >
                {displayState !== "attention" && (
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                )}
                {progressLabel}
              </span>
            ) : (
              progressLabel
            )}
          </small>
          {!locked && (
            <small className={styles.progressPercent}>{progress}%</small>
          )}
          {locked && (
            <small
              className={`${styles.progressPercent} ${styles.progressPercentHidden}`}
              aria-hidden="true"
            >
              0%
            </small>
          )}
        </div>
        <div className={styles.progressContainer}>
          <LinearProgress
            variant="determinate"
            value={progress !== 0 ? progress : 1}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: locked ? "#CBD5E1" : "#E2E8F0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: locked ? "#94A3B8" : color,
                borderRadius: 3,
                transition: "transform 0.4s ease",
              },
            }}
          />
        </div>
      </div>

      <div className={styles.cardFooter} aria-hidden={locked}>
        <FontAwesomeIcon
          icon={faCheckSquare}
          className={`${styles.checkIcon} ${locked ? styles.footerHidden : ""}`}
        />
        <small className={`${styles.stepsText} ${locked ? styles.footerHidden : ""}`}>
          <span className={styles.completedSteps}>{completedSteps}</span>/
          {totalSteps}
        </small>
      </div>

      {locked && lockTooltip && (
        <ReactTooltip id={tooltipId} type="info" place="top" effect="float">
          {lockTooltip}
        </ReactTooltip>
      )}
    </div>
  );
};

export default serviceCaller(withStyles(styles)(OnboardingCard));
